import { MessageCircle, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import * as Icons from "lucide-react";
import { COMPANY, LOGO_URL, STATS, TRUST_BADGES, FLOATING_ICONS, openWhatsApp } from "@/lib/site";
import { TechArtefacts } from "@/components/effects/TechArtefacts";

export const Hero = () => {
  return (
    <section id="home" className="relative overflow-hidden pt-14 lg:pt-20 pb-24 lg:pb-32" data-testid="hero-section">
      {/* backgrounds */}
      <div className="absolute inset-0 mesh-bg" aria-hidden />
      <div className="absolute inset-0 grid-bg opacity-70" aria-hidden />
      <TechArtefacts />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left */}
        <div className="reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white gradient-border soft-shadow text-xs font-semibold text-slate-700 mb-6" data-testid="hero-eyebrow">
            <Sparkles className="w-3.5 h-3.5 text-brand-purple" />
            AI-powered digital partner for modern businesses
          </div>

          <h1 className="section-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-slate-900" data-testid="hero-heading">
            Transform Your Business with{" "}
            <span className="text-gradient">Smart Digital Solutions</span>
          </h1>

          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="hero-subheading">
            We build powerful websites, cloud platforms, AI automation, software, mobile apps, branding, digital marketing and growth systems for startups, SMEs and enterprises.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#contact" className="btn-gradient rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2" data-testid="hero-primary-cta">
              Get Free Consultation <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={COMPANY.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openWhatsApp}
              className="rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 bg-brand-whatsapp text-white shadow-lg shadow-emerald-500/30 hover:-translate-y-0.5 transition-all"
              data-testid="hero-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
          </div>

          {/* Trust badges */}
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-700" data-testid="hero-trust-badges">
            {TRUST_BADGES.map((b) => (
              <div key={b} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{b}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="hero-stats">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-100 soft-shadow px-4 py-5">
                <div className="text-3xl font-heading font-extrabold text-gradient">{s.value}</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right - illustration */}
        <div className="relative h-[520px] lg:h-[600px] reveal" data-testid="hero-illustration" style={{ transitionDelay: "150ms" }}>
          {/* Glow */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[420px] h-[420px] rounded-full bg-brand-gradient opacity-20 blur-3xl" />
          </div>

          {/* Central card */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-[320px] lg:w-[380px] aspect-[4/5] rounded-[28px] bg-white soft-shadow-lg border border-slate-100 overflow-hidden">
              {/* Header bar */}
              <div className="h-8 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5 px-3">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <img src={LOGO_URL} alt="logo" className="w-10 h-10 rounded-xl" />
                  <div>
                    <div className="font-heading font-bold text-sm text-slate-900">Growth Dashboard</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Live · Q1 2026</div>
                  </div>
                </div>
                {/* Fake analytics */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[10px] text-slate-500">Traffic</div>
                    <div className="font-heading font-bold text-slate-900">+38%</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[10px] text-slate-500">Leads</div>
                    <div className="font-heading font-bold text-slate-900">1.2k</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[10px] text-slate-500">CVR</div>
                    <div className="font-heading font-bold text-slate-900">4.8%</div>
                  </div>
                </div>
                {/* Fake chart */}
                <div className="rounded-xl bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4 h-32 relative">
                  <svg viewBox="0 0 200 80" className="w-full h-full">
                    <defs>
                      <linearGradient id="g1" x1="0" x2="1">
                        <stop offset="0%" stopColor="#2563EB" />
                        <stop offset="50%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#DB2777" />
                      </linearGradient>
                    </defs>
                    <polyline
                      fill="none"
                      stroke="url(#g1)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      points="0,60 20,55 40,45 60,50 80,35 100,40 120,25 140,30 160,15 180,20 200,8"
                    />
                    <polyline
                      fill="url(#g1)"
                      opacity="0.15"
                      points="0,60 20,55 40,45 60,50 80,35 100,40 120,25 140,30 160,15 180,20 200,8 200,80 0,80"
                    />
                  </svg>
                </div>
                {/* Feature chips */}
                <div className="flex flex-wrap gap-1.5">
                  {["AI", "Cloud", "Analytics", "SEO"].map((chip) => (
                    <span key={chip} className="text-[10px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{chip}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Floating icons */}
          {FLOATING_ICONS.map((f, i) => {
            const Icon = Icons[f.icon];
            return (
              <div
                key={i}
                className="absolute animate-float-slow"
                style={{ left: f.x, top: f.y, animationDelay: f.delay }}
              >
                <div className="glass-card rounded-2xl px-3 py-2.5 flex items-center gap-2 soft-shadow">
                  <span className="w-7 h-7 rounded-lg bg-brand-gradient flex items-center justify-center text-white">
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-xs font-semibold text-slate-800">{f.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
